import { useState, useEffect, useContext, useRef } from 'react';
import shallowEqual from 'shallowequal';
import EasyPeasyContext from './context';
import { isStateObject } from './lib';

export function createStoreStateHook(Context) {
  return function useStoreState(mapState, dependencies = []) {
    const store = useContext(Context);
    const [state, setState] = useState(() => mapState(store.getState()));
    const [error, setError] = useState(null);
    // As our effect only fires on mount and unmount it won't have the state
    // changes visible to it, therefore we use a mutable ref to track this.
    const stateRef = useRef(state);
    // Helps avoid firing of events when unsubscribed, i.e. unmounted
    const isActive = useRef(true);
    // Tracks when a hooked component is unmounting
    const unmounted = useRef(false);
    // Throwing the error inline allows error boundaries the opportunity to
    // catch the error
    if (error) {
      throw error;
    }
    useEffect(() => {
      isActive.current = true;
      const calculateState = () => {
        if (!isActive.current) {
          return;
        }
        try {
          const newState = mapState(store.getState());
          if (
            newState === stateRef.current ||
            (isStateObject(newState) &&
              isStateObject(stateRef.current) &&
              shallowEqual(newState, stateRef.current))
          ) {
            // Do nothing
            return;
          }
          stateRef.current = newState;
          setState(() => stateRef.current);
        } catch (err) {
          // see https://github.com/reduxjs/react-redux/issues/1179
          // There is a possibility mapState will fail as the props/state that
          // the component has received is stale. Therefore we will afford the
          // application a small window of opportunity, where if they unmount
          // the component or provide it with new "valid" props (which will
          // subsequently cause a refreshed subscription cycle) then we will not
          // throw an error.
          // This is by no means a robust solution. We should track the
          // associated issue in the hope for a more dependable solution.

          // Setting the listener as "inactive", this can only be changed if the
          // incoming dependencies are different (i.e. props have changed)
          isActive.current = false;

          setTimeout(() => {
            if (!unmounted.current && !isActive.current) {
              setError(err);
            }
          }, 200); // give a window of opportunity
        }
      };
      calculateState();
      const unsubscribe = store.subscribe(calculateState);
      return () => {
        isActive.current = false;
        unsubscribe();
      };
    }, dependencies);

    // This effect will set the ref value to indicate that the component has
    // unmounted
    useEffect(() => {
      return () => {
        unmounted.current = true;
      };
    }, []);
    return state;
  };
}

export const useStoreState = createStoreStateHook(EasyPeasyContext);

export function createStoreActionsHook(Context) {
  return function useStoreActions(mapActions) {
    const store = useContext(Context);
    return mapActions(store.dispatch);
  };
}

export const useStoreActions = createStoreActionsHook(EasyPeasyContext);

export function createStoreDispatchHook(Context) {
  return function useStoreDispatch() {
    const store = useContext(Context);
    return store.dispatch;
  };
}

export const useStoreDispatch = createStoreDispatchHook(EasyPeasyContext);

const wrapWithWarning = (prev, next, useImp) => {
  let warned = false;
  return function useDeprecated(...args) {
    if (process.env.NODE_ENV !== 'production' && !warned) {
      // eslint-disable-next-line no-console
      console.warn(
        `easy-peasy: The "${prev}" hook has been deprecated. Please replace all instances with the "${next}" hook.`,
      );
      warned = true;
    }
    return useImp(...args);
  };
};

export const useActions = wrapWithWarning(
  'useActions',
  'useStoreActions',
  useStoreActions,
);

export const useDispatch = wrapWithWarning(
  'useDispatch',
  'useStoreDispatch',
  useStoreDispatch,
);

export const useStore = wrapWithWarning(
  'useStore',
  'useStoreState',
  useStoreState,
);

export function createTypedHooks() {
  return {
    useActions,
    useDispatch,
    useStore,
    useStoreActions,
    useStoreDispatch,
    useStoreState,
  };
}
